import { useState, type ChangeEvent, type FormEvent } from 'react';
import { validarSenha } from '@agencia/shared';
import { useAuth } from '@/auth/AuthContext';
import { Alert, AuthLayout, Button, Field } from '@/components/ui';
import { api } from '@/lib/api';
import { errorMessage } from '@/lib/errors';

/**
 * Troca de senha. Obrigatória no primeiro login com senha temporária;
 * também acessível pelo menu do usuário.
 */
export function ChangePasswordPage({ required, onDone }: { required: boolean; onDone?: () => void }) {
  const { state, refresh, logout } = useAuth();
  const email = state.status === 'authenticated' ? state.user.email : undefined;
  const [form, setForm] = useState({ senhaAtual: '', novaSenha: '', confirmacao: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const erros = form.novaSenha ? validarSenha(form.novaSenha, email) : [];
  const confirmacaoErro = form.confirmacao && form.confirmacao !== form.novaSenha ? 'As senhas não conferem.' : undefined;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (erros.length || confirmacaoErro) return;
    setLoading(true);
    try {
      await api.post('/auth/password', { senhaAtual: form.senhaAtual, novaSenha: form.novaSenha });
      setOk(true);
      setForm({ senhaAtual: '', novaSenha: '', confirmacao: '' });
      await refresh();
      onDone?.();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  const set = (k: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => setForm({ ...form, [k]: e.target.value });

  const content = (
    <form className="stack" onSubmit={(e) => void submit(e)} noValidate>
      {error && <Alert>{error}</Alert>}
      {ok && !required && <Alert kind="success">Senha alterada. Sessões em outros dispositivos foram encerradas.</Alert>}
      <Field
        label={required ? 'Senha temporária' : 'Senha atual'}
        type="password"
        value={form.senhaAtual}
        onChange={set('senhaAtual')}
        autoComplete="current-password"
        required
      />
      <Field
        label="Nova senha"
        type="password"
        value={form.novaSenha}
        onChange={set('novaSenha')}
        autoComplete="new-password"
        hint="Mínimo de 10 caracteres, com letras e números."
        error={erros[0]}
        required
      />
      <Field
        label="Confirme a nova senha"
        type="password"
        value={form.confirmacao}
        onChange={set('confirmacao')}
        autoComplete="new-password"
        error={confirmacaoErro}
        required
      />
      <Button type="submit" loading={loading}>
        Salvar nova senha
      </Button>
      {required && (
        <Button type="button" variant="ghost" onClick={() => void logout()}>
          Sair
        </Button>
      )}
    </form>
  );

  if (required) {
    return (
      <AuthLayout title="Crie sua senha" subtitle="Você entrou com uma senha temporária. Defina agora uma senha só sua.">
        {content}
      </AuthLayout>
    );
  }
  return (
    <section className="card narrow">
      <h1 className="page-title">Trocar senha</h1>
      {content}
    </section>
  );
}
